import type { Path } from 'react-router';
import { parseUIAiConfigFilterFromAPI } from '@gonfalon/ai-configs/filters';
import { projectContextSchema } from '@gonfalon/app-context/projectContext';
import { parseUIContextViewFromAPI } from '@gonfalon/contexts/kinds/parseUIContextViewFromAPI';
import { parseUIFlagListViewFromAPI } from '@gonfalon/flag-filters/parseUIFlagListViewFromAPI';
import type { schemas } from '@gonfalon/openapi/types';
import { parseUISegmentViewFromAPI } from '@gonfalon/segments/parseUISegmentViewFromAPI';
import type { IconName } from '@launchpad-ui/icons';
import { z } from 'zod';

type APIShortcut = schemas['ShortcutRep'];

const flagShortcutSchema = z.object({
  type: z.literal('flag'),
  name: z.string(),
  icon: z.string().transform((icon) => icon as IconName),
  key: z.string(),
  visibility: z.literal('me'),
  entityKey: z.string(),
  context: projectContextSchema,
});

const flagListShortcutSchema = z.object({
  type: z.literal('flags'),
  name: z.string(),
  icon: z.string().transform((icon) => icon as IconName),
  key: z.string(),
  visibility: z.literal('me'),
  filters: z
    .object({})
    .passthrough()
    .optional()
    .transform((filters) => (filters ? parseUIFlagListViewFromAPI(filters) : undefined)),
  context: projectContextSchema,
});

const contextListShortcutSchema = z.object({
  type: z.literal('contexts'),
  name: z.string(),
  icon: z.string().transform((icon) => icon as IconName),
  key: z.string(),
  visibility: z.literal('me'),
  filters: z
    .object({})
    .passthrough()
    .optional()
    .transform((filters) => (filters ? parseUIContextViewFromAPI(filters) : undefined)),
  context: projectContextSchema,
});

const segmentsShortcutSchema = z.object({
  type: z.literal('segments'),
  name: z.string(),
  icon: z.string().transform((icon) => icon as IconName),
  key: z.string(),
  visibility: z.literal('me'),
  filters: z
    .object({})
    .passthrough()
    .optional()
    .transform((filters) => (filters ? parseUISegmentViewFromAPI(filters) : undefined)),
  context: projectContextSchema,
});

const viewShortcutSchema = z.object({
  type: z.literal('view'),
  name: z.string(),
  icon: z.string().transform((icon) => icon as IconName),
  key: z.string(),
  visibility: z.literal('me'),
  entityKey: z.string(),
  context: projectContextSchema,
});

const aiConfigsShortcutSchema = z.object({
  type: z.literal('ai-configs'),
  name: z.string(),
  icon: z.string().transform((icon) => icon as IconName),
  key: z.string(),
  visibility: z.literal('me'),
  filters: z
    .object({})
    .passthrough()
    .optional()
    .transform((filters, ctx) => (filters ? parseUIAiConfigFilterFromAPI(filters, ctx) : undefined)),
  context: projectContextSchema,
});

const aiConfigShortcutSchema = z.object({
  type: z.literal('ai-config'),
  name: z.string(),
  icon: z.string().transform((icon) => icon as IconName),
  key: z.string(),
  visibility: z.literal('me'),
  entityKey: z.string(),
  context: projectContextSchema,
});

const shortcutSchema = z.discriminatedUnion('type', [
  flagShortcutSchema,
  flagListShortcutSchema,
  contextListShortcutSchema,
  segmentsShortcutSchema,
  viewShortcutSchema,
  aiConfigsShortcutSchema,
  aiConfigShortcutSchema,
]);

/**
 * This helper parses the API shortcut data into a UI shortcut data.
 * While we could use the schema directly, we want to ensure we start from the OpenAPI schema.
 */
export function parseShortcut(data: APIShortcut) {
  return shortcutSchema.safeParse({
    type: data.type,
    name: data.name,
    icon: data.icon,
    key: data.key,
    visibility: data.visibility,
    filters: data.filters,
    context: data.context,
    entityKey: data.entityKey,
  });
}

type FlagListShortcut = z.infer<typeof flagListShortcutSchema>;
type ContextListShortcut = z.infer<typeof contextListShortcutSchema>;
type FlagShortcut = z.infer<typeof flagShortcutSchema>;
type SegmentsShortcut = z.infer<typeof segmentsShortcutSchema>;
type ViewShortcut = z.infer<typeof viewShortcutSchema>;
type AIConfigsShortcut = z.infer<typeof aiConfigsShortcutSchema>;
type AIConfigShortcut = z.infer<typeof aiConfigShortcutSchema>;

export type Shortcut =
  | FlagListShortcut
  | ContextListShortcut
  | FlagShortcut
  | SegmentsShortcut
  | ViewShortcut
  | AIConfigsShortcut
  | AIConfigShortcut;

export type UI<T extends Shortcut> = T & {
  path: Path;
};

export type UIFlagListShortcut = UI<FlagListShortcut>;
export type UIContextListShortcut = UI<ContextListShortcut>;
export type UIFlagShortcut = UI<FlagShortcut>;
export type UISegmentsShortcut = UI<SegmentsShortcut>;
export type UIViewShortcut = UI<ViewShortcut>;
export type UIAIConfigsShortcut = UI<AIConfigsShortcut>;
export type UIAIConfigShortcut = UI<AIConfigShortcut>;

export type UIShortcut =
  | UIFlagListShortcut
  | UIContextListShortcut
  | UIFlagShortcut
  | UISegmentsShortcut
  | UIViewShortcut
  | UIAIConfigsShortcut
  | UIAIConfigShortcut;
